import React, { useState } from "react";
import { cn } from "@/lib/utils";
import { ChevronDown } from "lucide-react";

interface FaqItem {
  question: string;
  answer: string;
}

const faqItems: FaqItem[] = [
  {
    question: "How does the performance optimization work?",
    answer: "Our platform uses proprietary algorithms to analyze your system and game settings, then makes real-time adjustments to optimize resource allocation, reduce input lag, and enhance frame rates for a smoother gaming experience."
  },
  {
    question: "Is this platform compatible with all games?",
    answer: "Our platform is compatible with most modern PC games. We regularly update our compatibility list to include new releases. You can check specific game compatibility on our support page."
  },
  {
    question: "Will using this platform risk my gaming accounts?",
    answer: "No. We've designed our platform with security as a top priority. Our technology uses permitted enhancement methods that comply with the terms of service of most games. We do not modify game files or interact with game code in prohibited ways."
  },
  {
    question: "Can I use this on multiple computers?",
    answer: "Each subscription allows you to use the platform on up to 2 devices simultaneously. Our premium plans offer options for additional devices if needed."
  },
  {
    question: "How often do you release updates?",
    answer: "We release minor updates weekly to ensure compatibility with the latest game patches. Major feature updates are typically released quarterly with advance notice to our users."
  },
  {
    question: "What if I'm not satisfied with the service?",
    answer: "We offer a 7-day free trial and a 30-day money-back guarantee for all new subscribers. If you're not completely satisfied with our service, you can request a full refund within this period, no questions asked."
  },
  {
    question: "Do you offer customer support?",
    answer: "Yes, we provide customer support through various channels. Basic plans include standard email support, while premium plans feature priority support with faster response times and direct access to our technical team."
  },
];

export const FaqSection = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggleItem = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section 
      id="faq" 
      className="py-20 md:py-32"
    >
      <div className="container max-w-4xl mx-auto px-6">
        <div className="text-center mb-16">
          <div className="mb-3">
            <span className="inline-block px-3 py-1 text-xs font-medium text-purple-light rounded-full border border-purple-light/20">
              FAQ
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold mb-4 font-display text-white">
            Frequently Asked <span className="text-gradient">Questions</span>
          </h2>
          <p className="text-purple-light/70 max-w-2xl mx-auto">
            Find answers to common questions about our platform, features, and services.
          </p>
        </div>
        
        <div className="space-y-4">
          {faqItems.map((item, index) => (
            <div 
              key={index}
              className={cn(
                "border border-purple-light/10 rounded-xl overflow-hidden transition-all duration-300",
                openIndex === index ? "shadow-lg shadow-purple-900/10" : ""
              )}
            >
              <button
                className="w-full px-6 py-4 text-left flex justify-between items-center text-purple-light hover:text-white transition-colors"
                onClick={() => toggleItem(index)}
              >
                <span className="font-medium text-lg">{item.question}</span>
                <ChevronDown className={cn(
                  "h-5 w-5 text-purple-light/50 transition-transform duration-300",
                  openIndex === index ? "transform rotate-180" : ""
                )} />
              </button>
              <div className={cn(
                "px-6 transition-all duration-300 overflow-hidden",
                openIndex === index ? "pb-4 max-h-96" : "max-h-0"
              )}>
                <p className="text-purple-light/70">{item.answer}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
